import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Bell, MessageSquare, UserPlus, PhoneMissed, Volume2, VolumeX } from 'lucide-react';
import { getSocket } from '../utils/socket';
import { setTotalUnread } from '../store/inboxSlice';
import { pushNotification, markAllSeen, markSeen, clearNotifications } from '../store/notificationsSlice';
import { playPing, isSoundOn, setSoundOn } from '../utils/sound';
import { useT } from '../utils/translate';
import { usePermissions } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const ICON = { message: MessageSquare, lead: UserPlus, call: PhoneMissed };
let seq = 0;
const nid = () => `n${Date.now()}-${seq++}`;

// Невидимый слушатель: подписывается на события организации и работает на любой странице (раньше новые сообщения замечала только страница «Входящие»).
// · счётчик непрочитанных «Входящих» подтягивается при входе и при возвращении на вкладку;
// · новое входящее сообщение, лид и пропущенный звонок попадают в ленту колокольчика, дают всплывашку и звук (если пользователь не в «Входящих»).
export function GlobalNotifications() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const { canSeeModule } = usePermissions();
  const orgId = useSelector((s) => s.auth.user?.organization?.id || s.auth.user?.organization?._id);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const canInbox = canSeeModule('inbox');

  // Счётчик непрочитанных во «Входящих»
  useEffect(() => {
    if (!orgId || !canInbox) return undefined;
    let alive = true;
    const load = async () => {
      if (document.hidden || pathRef.current.startsWith('/inbox')) return;   // на странице «Входящих» счётчик ведёт она сама
      try {
        const r = await axios.get(`${API}/inbox`, { params: { status: 'unanswered', limit: 50 } });
        if (alive) dispatch(setTotalUnread((r.data.conversations || []).reduce((s, c) => s + (c.unreadCount || 0), 0)));
      } catch { /* сеть: повторим при следующем возвращении на вкладку */ }
    };
    load();
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; document.removeEventListener('visibilitychange', load); };
  }, [orgId, canInbox, dispatch]);

  useEffect(() => {
    if (!orgId) return undefined;
    const socket = getSocket();
    const notify = (item, ping = true) => {
      const n = { id: nid(), ...item };
      dispatch(pushNotification(n));
      if (ping) playPing();
      toast.custom((tt) => (
        <button type="button" onClick={() => { toast.dismiss(tt.id); dispatch(markSeen(n.id)); navigate(n.to); }}
          className={`${tt.visible ? 'animate-pop-in' : 'opacity-0'} max-w-sm w-full flex items-start gap-3 text-left bg-card border border-surface-200 rounded-xl shadow-glass px-4 py-3`}>
          <span className="mt-0.5 w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">{React.createElement(ICON[n.type] || Bell, { className: 'w-4 h-4' })}</span>
          <span className="min-w-0"><span className="block text-sm font-semibold text-ink">{t(`notifCenter.${n.type}`)}</span><span className="block text-sm text-ink-secondary truncate">{n.text}</span></span>
        </button>
      ), { duration: 6000 });
    };
    const onMessage = ({ message } = {}) => {
      if (!message || message.direction !== 'in' || message.isNote) return;
      if (pathRef.current.startsWith('/inbox')) return;       // там сообщение и так на экране
      dispatch((d, getState) => d(setTotalUnread((getState().inbox?.totalUnread || 0) + 1)));
      notify({ type: 'message', text: (message.text || '').slice(0, 90) || t('notifCenter.media'), to: '/inbox' });
    };
    const onLead = ({ deal, funnelId } = {}) => {
      if (!deal) return;
      notify({ type: 'lead', text: deal.title || deal.name || deal.contact?.name || '', to: funnelId ? `/funnel/${funnelId}` : '/dashboard' }, false);
    };
    const onCallEnded = ({ status } = {}) => {
      if (status !== 'missed') return;
      notify({ type: 'call', text: t('notifCenter.missedCallText'), to: '/calls' }, false);
    };
    socket.on('inbox:message', onMessage);
    socket.on('deal:created', onLead);
    socket.on('atc:ended', onCallEnded);
    return () => { socket.off('inbox:message', onMessage); socket.off('deal:created', onLead); socket.off('atc:ended', onCallEnded); };
  }, [orgId, dispatch, navigate, t]);

  return null;
}

function timeAgo(ts, t) {
  const m = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (m < 1) return t('notifCenter.now');
  if (m < 60) return `${m} ${t('notifCenter.min')}`;
  return `${Math.round(m / 60)} ${t('notifCenter.hour')}`;
}

// Колокольчик в верхней панели: лента событий, сводка непрочитанных «Входящих», переключатель звука.
export default function NotificationBell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useT();
  const items = useSelector((s) => s.notifications.items);
  const inboxUnread = useSelector((s) => s.inbox?.totalUnread || 0);
  const { canSeeModule } = usePermissions();
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(isSoundOn());
  const ref = useRef(null);
  // Сообщения считаются через «непрочитанные во Входящих» (они не гаснут, пока не прочитаны в чате); лиды и пропущенные — пока не открыта лента
  const unseenOther = items.filter((i) => !i.seen && i.type !== 'message').length;
  const badge = unseenOther + (canSeeModule('inbox') ? inboxUnread : 0);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [open]);

  const toggle = () => { setOpen((v) => !v); if (!open) dispatch(markAllSeen()); };
  const go = (to) => { setOpen(false); navigate(to); };
  const flip = () => { const n = !sound; setSound(n); setSoundOn(n); if (n) playPing(); };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={toggle} aria-haspopup="true" aria-expanded={open} aria-label={t('notifCenter.title')} title={t('notifCenter.title')}
        className="relative flex items-center justify-center h-8 w-8 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-colors">
        <Bell className="w-[18px] h-[18px]" />
        {badge > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold leading-4 text-center">{badge > 99 ? '99+' : badge}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-[min(22rem,calc(100vw-1.5rem))] glass-strong rounded-2xl shadow-pop overflow-hidden z-50 animate-pop-in">
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
            <p className="text-sm font-semibold text-ink">{t('notifCenter.title')}</p>
            <div className="flex items-center gap-1">
              <button type="button" onClick={flip} aria-pressed={sound} aria-label={t(sound ? 'notifCenter.soundOff' : 'notifCenter.soundOn')} title={t(sound ? 'notifCenter.soundOff' : 'notifCenter.soundOn')}
                className="p-1.5 rounded-lg text-ink-tertiary hover:bg-surface-100 hover:text-ink">{sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
              {items.length > 0 && <button type="button" onClick={() => dispatch(clearNotifications())} className="text-xs text-ink-tertiary hover:text-ink px-1.5 py-1">{t('notifCenter.clear')}</button>}
            </div>
          </div>
          {canSeeModule('inbox') && inboxUnread > 0 && (
            <button type="button" onClick={() => go('/inbox')} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-100 border-b border-surface-200">
              <span className="w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center"><MessageSquare className="w-4 h-4" /></span>
              <span className="text-sm text-ink">{t('notifCenter.unreadInbox')}: <b>{inboxUnread}</b></span>
            </button>
          )}
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 && !(inboxUnread > 0) && <p className="px-4 py-8 text-center text-sm text-ink-tertiary">{t('notifCenter.empty')}</p>}
            {items.map((n) => {
              const Icon = ICON[n.type] || Bell;
              return (
                <button key={n.id} type="button" onClick={() => go(n.to)} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-surface-100 border-b border-surface-100 last:border-0">
                  <span className="mt-0.5 w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
                  <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-ink">{t(`notifCenter.${n.type}`)}</span><span className="block text-sm text-ink-secondary truncate">{n.text}</span></span>
                  <span className="text-xs text-ink-tertiary shrink-0">{timeAgo(n.at, t)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
